import { useState } from 'react';
import { tenders as tendersApi } from '../lib/api';
import { toast } from 'sonner';
import { Gavel, Award, Trophy, Trash2, Clock } from 'lucide-react';
import PriceTooltip from './PriceTooltip';

const qualities = ['premium', 'standard', 'economy'];

export default function OwnerTendersPanel({ tenders, store, onRefresh }) {
  if (!store) return null;
  const matching = tenders.filter(t => t.category === store.category && t.village === store.village);

  return (
    <section className="card-soft p-5">
      <div className="flex items-center gap-2 mb-3">
        <Gavel className="w-5 h-5 text-[#E67E22]"/>
        <div className="font-heading font-bold text-lg">Quote requests in your village</div>
        <span className="pill !text-[10px] ml-auto">{matching.length} live</span>
      </div>

      {matching.length === 0 ? (
        <div className="text-sm text-ink/60 mt-2">No live requests in {store.village} for {store.category} right now.</div>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-auto pr-1">
          {matching.map(t => <TenderRow key={t.id} tender={t} store={store} onRefresh={onRefresh}/>)}
        </div>
      )}
    </section>
  );
}

function TenderRow({ tender, store, onRefresh }) {
  const my = tender.my_bid;
  const [open, setOpen] = useState(!my);
  const [form, setForm] = useState({
    price: my?.price ?? '',
    discount_pct: my?.discount_pct ?? 0,
    offer_text: my?.offer_text ?? '',
    quality_grade: my?.quality_grade ?? 'standard',
    delivery_minutes: my?.delivery_minutes ?? 60,
    notes: my?.notes ?? '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.price) { toast.error('Enter a price'); return; }
    setBusy(true);
    try {
      await tendersApi.bid(tender.id, {
        store_id: store.id,
        price: parseFloat(form.price),
        discount_pct: parseFloat(form.discount_pct) || 0,
        offer_text: form.offer_text,
        quality_grade: form.quality_grade,
        delivery_minutes: parseInt(form.delivery_minutes) || 60,
        notes: form.notes,
      });
      toast.success(my ? 'Bid updated' : 'Bid placed');
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit bid');
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    try { await tendersApi.withdrawBid(tender.id); toast.success('Bid withdrawn'); onRefresh?.(); }
    catch { toast.error('Could not withdraw'); }
  };

  const expired = new Date(tender.expires_at).getTime() <= Date.now();
  const fmt = (ms) => { const m = Math.floor(ms/60000); return m > 0 ? `${m}m left` : 'Closing'; };

  return (
    <div data-testid={`owner-tender-${tender.id}`} className="rounded-lg border border-border bg-white p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-semibold text-base">
            <PriceTooltip name={tender.product_name} category={tender.category} unit={tender.unit} testIdSuffix={tender.id}>
              {tender.product_name}
            </PriceTooltip>
          </div>
          <div className="text-xs text-ink/60">{tender.quantity} × {tender.unit}{tender.budget && <> · budget ₹{tender.budget}</>}</div>
          {tender.items && tender.items.length > 0 && (
            <div className="mt-1 text-xs text-ink/70 leading-relaxed">
              + {tender.items.map((i, idx) => (
                <span key={`${tender.id}-it-${idx}`}>
                  <PriceTooltip name={i.name} category={tender.category} unit={i.unit} testIdSuffix={`${tender.id}-${idx}`}>
                    {i.name}
                  </PriceTooltip>
                  <span className="text-ink/50"> ×{i.quantity} {i.unit}</span>
                  {idx < tender.items.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
          {tender.description && <div className="text-xs text-ink/70 mt-1">"{tender.description}"</div>}
        </div>
        <div className="text-right">
          <div className="text-xs text-ink/60 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{expired ? 'Expired' : fmt(new Date(tender.expires_at).getTime() - Date.now())}</div>
          <div className="text-[10px] uppercase tracking-wider text-ink/40 mt-0.5">{tender.total_bids} bid{tender.total_bids !== 1 ? 's' : ''}</div>
        </div>
      </div>

      {my && !open && (
        <div className="mt-3 rounded-md bg-[#FFF7EE] p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <div className="inline-flex items-center gap-2">
              <span className="font-bold">Your bid:</span>
              <span>₹{my.price}{my.discount_pct > 0 && <span className="text-[#1B5E20] ml-1">({my.discount_pct}% off)</span>}</span>
              <span className="text-xs text-ink/60 capitalize">· {my.quality_grade}</span>
            </div>
            {my.offer_text && <div className="text-xs text-ink/60 mt-0.5">Offer: {my.offer_text}</div>}
            <div className="text-xs mt-1 inline-flex items-center gap-1">
              {tender.my_rank === 1 ? <span className="inline-flex items-center gap-1 text-[#1B5E20] font-bold"><Trophy className="w-3.5 h-3.5"/>You're winning!</span>
                : <span className="text-ink/70">Rank #{tender.my_rank} of {tender.total_bids}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid={`edit-bid-${tender.id}`} onClick={() => setOpen(true)} className="btn-outline !py-1.5 !px-3 !text-xs">Update bid</button>
            <button data-testid={`withdraw-bid-${tender.id}`} onClick={withdraw} className="text-xs text-destructive inline-flex items-center gap-1 px-2"><Trash2 className="w-3 h-3"/>Withdraw</button>
          </div>
        </div>
      )}

      {open && !expired && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Field label="Price (₹/unit)" testId={`bid-price-${tender.id}`}>
            <input type="number" min="0" value={form.price} onChange={e=>setForm({...form, price:e.target.value})} className="inp"/>
          </Field>
          <Field label="Discount %" testId={`bid-discount-${tender.id}`}>
            <input type="number" min="0" max="100" value={form.discount_pct} onChange={e=>setForm({...form, discount_pct:e.target.value})} className="inp"/>
          </Field>
          <Field label="Quality">
            <select data-testid={`bid-quality-${tender.id}`} value={form.quality_grade} onChange={e=>setForm({...form, quality_grade:e.target.value})} className="inp">
              {qualities.map(q => <option key={q} value={q}>{q[0].toUpperCase()+q.slice(1)}</option>)}
            </select>
          </Field>
          <Field label="Delivery (min)" testId={`bid-delivery-${tender.id}`}>
            <input type="number" min="0" value={form.delivery_minutes} onChange={e=>setForm({...form, delivery_minutes:e.target.value})} className="inp"/>
          </Field>
          <div className="col-span-2"><Field label="Offer (free item, combo etc.)" testId={`bid-offer-${tender.id}`}>
            <input value={form.offer_text} onChange={e=>setForm({...form, offer_text:e.target.value})} placeholder="Free dhaniya with order" className="inp"/>
          </Field></div>
          <button data-testid={`submit-bid-${tender.id}`} disabled={busy} onClick={submit} className="btn-primary col-span-2"><Award className="w-4 h-4"/>{busy ? 'Saving…' : (my ? 'Update my bid' : 'Place bid')}</button>
          {my && <button onClick={() => setOpen(false)} className="btn-outline col-span-2 !py-2">Cancel</button>}
        </div>
      )}

      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:white;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;outline:none}.inp:focus{border-color:#E67E22;box-shadow:0 0 0 3px rgba(230,126,34,0.15)}`}</style>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">{label}</div>
      {children}
    </label>
  );
}
